/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Delete from '@material-ui/icons/Delete';
import Edit from '@material-ui/icons/Edit';
import Publish from '@material-ui/icons/CloudUpload';
import Refresh from '@material-ui/icons/Refresh';
import SendToReview from '@material-ui/icons/Visibility';
import Star from '@material-ui/icons/Star';
import UnPublish from '@material-ui/icons/CloudDownload';
import Tooltip from '@material-ui/core/Tooltip';
import Zoom from '@material-ui/core/Zoom';
import LinearProgress from '@material-ui/core/LinearProgress/LinearProgress';
import withStyles from '@material-ui/core/styles/withStyles';
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import Card from 'components/UI/Card/Card';
import CardBody from 'components/UI/Card/CardBody';
import GridItem from 'components/UI/Grid/GridItem';
import GridContainer from 'components/UI/Grid/GridContainer';
import Button from 'components/UI/CustomButtons/Button';
import CustomInput from 'components/UI/CustomInput/CustomInput';
import Snackbar from 'components/UI/Snackbar/Snackbar';
import Badge from 'components/UI/Badge/Badge';
import { LoadingAnimation } from 'components/common/Loading';
import WithPermission from 'components/User/WithPermission';
import RouterContext from 'helpers/routerContext';
import dateFormat from 'helpers/dateHelper';
import { USER } from 'routes/paths';
import { formatRoute } from 'react-router-named-routes';
import {
  formatMatchedRules,
  getReviewActions,
  reviewActionPermissions,
  reviewActions,
} from 'components/Review/helpers';

const actionIcons = {
  [reviewActions.PUBLISH]: Publish,
  [reviewActions.UNPUBLISH]: UnPublish,
  [reviewActions.REPROCESS]: Refresh,
  [reviewActions.SEND_TO_REVIEW]: SendToReview,
  [reviewActions.EDIT]: Edit,
  [reviewActions.DELETE]: Delete,
};

const actionColors = {
  [reviewActions.PUBLISH]: 'success',
  [reviewActions.UNPUBLISH]: 'danger',
  [reviewActions.REPROCESS]: 'warning',
  [reviewActions.SEND_TO_REVIEW]: 'primary',
  [reviewActions.EDIT]: 'info',
  [reviewActions.DELETE]: 'danger',
};

class ReviewDetails extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { data } = props;
    this.state = {
      dataId: data.id,
      editing: false,
      editRate: data.rate || '',
      editComment: data.comment || '',
      editErrors: [],
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { data } = nextProps;
    if (data.id !== prevState.dataId) {
      return {
        dataId: data.id,
        editing: false,
        editRate: data.rate || '',
        editComment: data.comment || '',
        editErrors: [],
      };
    }
    return null;
  }

  handleReadUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  handleAction = action => {
    const {
      publishReview,
      unpublishReview,
      reprocessReview,
      sendReviewToReview,
      deleteReview,
    } = this.props;
    if (action === reviewActions.PUBLISH) publishReview();
    if (action === reviewActions.UNPUBLISH) unpublishReview();
    if (action === reviewActions.REPROCESS) reprocessReview();
    if (action === reviewActions.SEND_TO_REVIEW) sendReviewToReview();
    if (action === reviewActions.DELETE) deleteReview();
    if (action === reviewActions.EDIT) {
      const { data } = this.props;
      this.setState({
        editing: true,
        editRate:
          data.rate === null || data.rate === undefined ? '' : data.rate,
        editComment: data.comment || '',
        editErrors: [],
      });
    }
  };

  handleSave = () => {
    const { updateReview } = this.props;
    const { editRate, editComment } = this.state;
    const normalizedRate =
      editRate === null || editRate === undefined ? '' : `${editRate}`.trim();
    const parsedRate =
      normalizedRate === '' ? undefined : Number(normalizedRate);

    if (
      parsedRate !== undefined &&
      (!Number.isInteger(parsedRate) || parsedRate < 1 || parsedRate > 5)
    ) {
      this.setState({
        editErrors: [
          {
            field: 'reviews.fields.rate',
            messages: ['reviews.errors.invalidRate'],
          },
        ],
      });
      return Promise.resolve();
    }

    return updateReview({
      rate: parsedRate,
      comment: editComment,
    }).then(({ errors }) => {
      if (errors && errors.length) {
        this.setState({ editErrors: errors });
        return;
      }
      this.setState({
        editing: false,
        editErrors: [],
      });
    });
  };

  renderActionButton = action => {
    const { classes, t, actionProcessing } = this.props;
    const Icon = actionIcons[action];
    const actionLabel = t(`reviews.actions.${action}`);
    return (
      <WithPermission
        key={`details-${action}`}
        allowPermissions={[reviewActionPermissions[action]]}
      >
        <Tooltip
          placement="top"
          TransitionComponent={Zoom}
          title={
            <React.Fragment>
              {actionLabel}
              <span className={classes.arrow} />
            </React.Fragment>
          }
          classes={{
            tooltip: classes.bootstrapTooltip,
            tooltipPlacementTop: classes.bootstrapPlacementTop,
          }}
        >
          <Button
            simple
            disabled={actionProcessing}
            onClick={() => this.handleAction(action)}
            id={`review-details-${action}`}
            color={actionColors[action]}
          >
            <Icon />
            <span>{actionLabel}</span>
          </Button>
        </Tooltip>
      </WithPermission>
    );
  };

  renderUser = (label, user) => {
    const { classes, t } = this.props;
    return (
      <div style={{ marginBottom: 16 }}>
        <h6 style={{ marginBottom: 4 }}>{label}</h6>
        {!user ? (
          <span>{t('common.unknown')}</span>
        ) : (
          <React.Fragment>
            <WithPermission
              alt={<p style={{ margin: 0 }}>{user.fullname}</p>}
              allowPermissions={['view_user']}
            >
              <Button
                simple
                color="primary"
                className={['read', classes.adBtns, classes.titleBtn].join(' ')}
                onClick={() => this.handleReadUser(user.id)}
              >
                {user.fullname}
              </Button>
            </WithPermission>
            <p style={{ margin: 0 }}>{user.email}</p>
            {user.phone && <p style={{ margin: 0 }}>{user.phone}</p>}
            <p style={{ margin: 0 }}>
              <b>{t('reviews.fields.rating')}:</b>{' '}
              {user.rate === null || user.rate === undefined ? '-' : user.rate}
            </p>
            <p style={{ margin: 0 }}>
              <b>{t('reviews.fields.reviewCount')}:</b>{' '}
              {user.reviewCount === null || user.reviewCount === undefined
                ? 0
                : user.reviewCount}
            </p>
          </React.Fragment>
        )}
      </div>
    );
  };

  renderDates = data => {
    const { t } = this.props;
    const fields = [
      ['createdOn', 'created'],
      ['updatedOn', 'updated'],
      ['publishedOn', 'published'],
      ['reviewedOn', 'reviewed'],
      ['lastReportedOn', 'lastReported'],
      ['deletedOn', 'deleted'],
    ];
    return fields.map(([field, label]) =>
      data[field] ? (
        <p key={field} style={{ margin: '0 0 4px' }}>
          <b>{t(`reviews.dates.${label}`)}:</b> {dateFormat(data[field], t)}
        </p>
      ) : null,
    );
  };

  renderRules = data => {
    const rules = formatMatchedRules(data.matchedRules);
    const { t } = this.props;
    if (!rules.length) return <p>{t('reviews.details.noRules')}</p>;
    return rules.map((rule, index) => (
      <p key={`details-rule-${index}`} style={{ margin: '0 0 4px' }}>
        {rule}
      </p>
    ));
  };

  renderReports = data => {
    const { t } = this.props;
    const reports = data.reports || [];
    if (!reports.length) return <p>{t('reviews.details.noReports')}</p>;
    return reports.map(report => (
      <div
        key={report.id}
        style={{
          borderBottom: '1px solid #eee',
          padding: '8px 0',
        }}
      >
        <p style={{ margin: 0 }}>
          <b>{t(`reviews.reportReasons.${report.reason}`)}</b>
          {report.status && ` - ${t(`reviews.reportStatus.${report.status}`)}`}
        </p>
        {report.comment && <p style={{ margin: '4px 0' }}>{report.comment}</p>}
        {report.reporter && (
          <p style={{ margin: 0 }}>
            {t('reviews.details.reporter')}: {report.reporter.fullname}
          </p>
        )}
        <p style={{ margin: 0 }}>
          {t('reviews.dates.created')}: {dateFormat(report.createdOn, t)}
        </p>
      </div>
    ));
  };

  renderEditForm = () => {
    const { classes, t, actionProcessing, data } = this.props;
    const { editRate, editComment, editErrors } = this.state;
    return (
      <Card>
        <CardBody>
          <h4>{t('reviews.details.editTitle')}</h4>
          {editErrors.map(error => (
            <p key={error.field} style={{ color: '#af223a' }}>
              <b>{t(error.field)}:</b>{' '}
              {error.messages.map(message => t(message)).join(', ')}
            </p>
          ))}
          <GridContainer>
            <GridItem xs={12} sm={4}>
              <CustomInput
                id="review-edit-rate"
                labelText={t('reviews.fields.rate')}
                formControlProps={{
                  className: classes.search,
                }}
                inputProps={{
                  type: 'number',
                  value: editRate,
                  inputProps: { min: 1, max: 5, step: 1 },
                  onChange: e => this.setState({ editRate: e.target.value }),
                }}
              />
            </GridItem>
            <GridItem xs={12}>
              <CustomInput
                id="review-edit-comment"
                labelText={t('reviews.fields.comment')}
                formControlProps={{
                  className: classes.search,
                  fullWidth: true,
                }}
                inputProps={{
                  multiline: true,
                  rows: 5,
                  value: editComment,
                  onChange: e => this.setState({ editComment: e.target.value }),
                }}
              />
            </GridItem>
          </GridContainer>
          <GridContainer justify="flex-end">
            <GridItem>
              <Button
                simple
                color="primary"
                disabled={actionProcessing}
                onClick={() =>
                  this.setState({
                    editing: false,
                    editRate: data.rate || '',
                    editComment: data.comment || '',
                    editErrors: [],
                  })
                }
                id="review-edit-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="primary"
                disabled={actionProcessing}
                onClick={this.handleSave}
                id="review-edit-save"
              >
                {t('common.save')}
              </Button>
            </GridItem>
          </GridContainer>
        </CardBody>
      </Card>
    );
  };

  render() {
    const {
      data,
      loading,
      classes,
      t,
      actionProcessing,
      failedIds,
      showNotification,
      closeNotification,
    } = this.props;
    const { editing } = this.state;
    const hasData = data && Object.keys(data).length > 0;
    const canEditReview =
      hasData &&
      getReviewActions(data.status).indexOf(reviewActions.EDIT) !== -1;

    return (
      <div className={classes.pageContainer}>
        <Snackbar
          place="tc"
          color="danger"
          message={t('common.failed_bulk_action', {
            count: failedIds.length,
          })}
          open={showNotification}
          closeNotification={closeNotification}
          close
        />
        <h3 className={classes.title}>{t('reviews.details.title')}</h3>
        <GridContainer>
          <GridItem xs={12}>
            <div
              className={[classes.loadingLineContainer, classes.topLine].join(
                ' ',
              )}
            >
              {(loading || actionProcessing) && (
                <LinearProgress
                  classes={{
                    barColorPrimary: classes.loadingLine,
                  }}
                />
              )}
            </div>
          </GridItem>
          {!hasData ? (
            <GridItem xs={12}>
              <Card>
                <CardBody>
                  <LoadingAnimation />
                </CardBody>
              </Card>
            </GridItem>
          ) : (
            <React.Fragment>
              <GridItem xs={12}>
                <Card>
                  <CardBody>
                    <GridContainer justify="space-between" alignItems="center">
                      <GridItem>
                        <h4 style={{ margin: 0 }}>
                          ID: {data.id}{' '}
                          <Badge color="info">
                            {t(`reviews.status.${data.status}`)}
                          </Badge>
                        </h4>
                      </GridItem>
                      <GridItem>
                        {getReviewActions(data.status).map(action =>
                          this.renderActionButton(action),
                        )}
                      </GridItem>
                    </GridContainer>
                  </CardBody>
                </Card>
              </GridItem>
              <GridItem xs={12} md={7}>
                <Card>
                  <CardBody>
                    <p
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 0,
                      }}
                    >
                      <Star style={{ width: 18, color: '#ffb500' }} />
                      <b style={{ marginLeft: 4 }}>
                        {t('reviews.fields.rate')}: {data.rate}
                      </b>
                    </p>
                    <h6>{t('reviews.fields.comment')}</h6>
                    <p style={{ whiteSpace: 'pre-wrap' }}>
                      {data.comment || t('reviews.list.emptyComment')}
                    </p>
                    <h6>{t('reviews.fields.rules')}</h6>
                    {this.renderRules(data)}
                    <h6>{t('reviews.fields.dates')}</h6>
                    {this.renderDates(data)}
                  </CardBody>
                </Card>
                {editing && canEditReview && this.renderEditForm()}
              </GridItem>
              <GridItem xs={12} md={5}>
                <Card>
                  <CardBody>
                    {this.renderUser(
                      t('reviews.fields.reviewer'),
                      data.reviewer,
                    )}
                    {this.renderUser(
                      t('reviews.fields.reviewee'),
                      data.reviewee,
                    )}
                    {this.renderUser(
                      t('reviews.fields.moderator'),
                      data.reviewedBy,
                    )}
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <h4>
                      {t('reviews.fields.reports')}{' '}
                      <Badge color={data.reportCount > 0 ? 'danger' : 'gray'}>
                        {data.reportCount}
                      </Badge>
                    </h4>
                    {this.renderReports(data)}
                  </CardBody>
                </Card>
              </GridItem>
            </React.Fragment>
          )}
        </GridContainer>
      </div>
    );
  }
}

ReviewDetails.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool.isRequired,
  actionProcessing: PropTypes.bool.isRequired,
  publishReview: PropTypes.func.isRequired,
  unpublishReview: PropTypes.func.isRequired,
  reprocessReview: PropTypes.func.isRequired,
  sendReviewToReview: PropTypes.func.isRequired,
  deleteReview: PropTypes.func.isRequired,
  updateReview: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  showNotification: PropTypes.bool.isRequired,
  closeNotification: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const ReviewDetailsWrapper = props => <ReviewDetails {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(ReviewDetailsWrapper),
);
